import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Form, Button } from 'react-bootstrap'
import useMccCodes from 'api/hooks/use-mcc-codes'

const normalizeValues = (value) => {
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

function MccSelectFilterPopover({
  column,
  params,
  setParams,
  onClose,
  onSetHandleApply,
  canEdit = true,
  hideApplyButton = false
}) {
  const accessorKey = column.accessor_key
  const modeKey = `${accessorKey}Mode`

  const { data: mccCodes, isLoading } = useMccCodes()

  const [currentValues, setCurrentValues] = useState(
    normalizeValues(params[accessorKey])
  )
  const [mode, setMode] = useState(params[modeKey] || 'include')
  const [search, setSearch] = useState('')

  const initialValues = useRef(normalizeValues(params[accessorKey]))
  const initialMode = useRef(params[modeKey] || 'include')

  const selectedSet = useMemo(() => new Set(currentValues), [currentValues])

  const filteredCodes = useMemo(() => {
    if (!mccCodes) return []
    if (!search) return mccCodes
    const lower = search.toLowerCase()
    return mccCodes.filter((item) => {
      const label = item.description || item.category || ''
      return (
        label.toLowerCase().includes(lower) ||
        (item.grouping || '').toLowerCase().includes(lower) ||
        item.mcc.includes(search)
      )
    })
  }, [mccCodes, search])

  const groupedCodes = useMemo(() => {
    const groups = new Map()
    filteredCodes.forEach((item) => {
      const key = item.grouping || 'Uncategorized'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(item)
    })
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [filteredCodes])

  const handleToggle = (mcc) => {
    if (!canEdit) return
    setCurrentValues((prev) =>
      prev.includes(mcc) ? prev.filter((v) => v !== mcc) : [...prev, mcc]
    )
  }

  const handleToggleGroup = (groupItems) => {
    if (!canEdit) return
    const codesInGroup = groupItems.map((i) => i.mcc)
    const allSelected = codesInGroup.every((c) => selectedSet.has(c))
    setCurrentValues((prev) => {
      if (allSelected) {
        return prev.filter((v) => !codesInGroup.includes(v))
      }
      const next = new Set(prev)
      codesInGroup.forEach((c) => next.add(c))
      return Array.from(next)
    })
  }

  const handleClear = () => {
    if (!canEdit) return
    setCurrentValues([])
    setMode('include')
    setParams((p) => ({ ...p, [accessorKey]: [], [modeKey]: 'include' }))
  }

  const handleApply = () => {
    const sameLength = currentValues.length === initialValues.current.length
    const sameValues =
      sameLength &&
      currentValues.every((val, idx) => val === initialValues.current[idx])
    const hasChanges = !sameValues || mode !== initialMode.current

    if (hasChanges) {
      setParams((p) => ({ ...p, [accessorKey]: currentValues, [modeKey]: mode }))
    }
    onClose()
  }

  useEffect(() => {
    if (onSetHandleApply) {
      onSetHandleApply(handleApply)
    }
  }, [currentValues, mode]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hideApplyButton) {
      setParams((p) => ({ ...p, [accessorKey]: currentValues, [modeKey]: mode }))
    }
  }, [currentValues, mode, hideApplyButton, setParams, accessorKey, modeKey])

  return (
    <div style={{ minWidth: '360px' }}>
      <div className="mb-2">
        <strong>{column.name}</strong>
      </div>

      <div className="mb-2">
        <Form.Select
          value={mode}
          onChange={(e) => canEdit && setMode(e.target.value)}
          size="sm"
          disabled={!canEdit}
        >
          <option value="include">Include selected</option>
          <option value="exclude">Exclude selected</option>
        </Form.Select>
      </div>

      <div className="d-flex gap-2 mb-2">
        <Form.Control
          type="text"
          size="sm"
          placeholder="Search by code, description, or group..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          variant="light"
          size="sm"
          onClick={handleClear}
          className="text-nowrap"
          disabled={!canEdit}
        >
          Clear All
        </Button>
      </div>

      <div className="text-muted small mb-2">
        {currentValues.length} of {mccCodes?.length || 0} codes selected
        {search && ` (showing ${filteredCodes.length} matches)`}
      </div>

      <div
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          border: '1px solid #dee2e6',
          borderRadius: '4px'
        }}
        className="mb-3"
      >
        {isLoading && (
          <div className="text-muted text-center py-3 small">Loading...</div>
        )}
        {!isLoading &&
          groupedCodes.map(([groupName, items]) => {
            const codesInGroup = items.map((i) => i.mcc)
            const selectedInGroup = codesInGroup.filter((c) =>
              selectedSet.has(c)
            ).length
            const allSelected =
              selectedInGroup === codesInGroup.length && codesInGroup.length > 0
            const someSelected =
              selectedInGroup > 0 && selectedInGroup < codesInGroup.length

            return (
              <div key={groupName} style={{ borderBottom: '1px solid #dee2e6' }}>
                <div
                  className="d-flex align-items-center px-2 py-2 bg-light"
                  style={{ position: 'sticky', top: 0, zIndex: 1 }}
                >
                  <Form.Check
                    type="checkbox"
                    id={`mcc-filter-group-${accessorKey}-${groupName}`}
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected
                    }}
                    onChange={() => handleToggleGroup(items)}
                    disabled={!canEdit}
                    label={
                      <span className="small fw-bold">
                        {groupName}
                        <span className="text-muted fw-normal ms-2">
                          ({selectedInGroup} of {items.length} selected)
                        </span>
                      </span>
                    }
                  />
                </div>
                {items.map((item) => (
                  <div
                    key={item.mcc}
                    className="d-flex align-items-center px-4 py-1"
                    style={{ borderTop: '1px solid #f0f0f0' }}
                  >
                    <Form.Check
                      type="checkbox"
                      id={`mcc-filter-${accessorKey}-${item.mcc}`}
                      checked={selectedSet.has(item.mcc)}
                      onChange={() => handleToggle(item.mcc)}
                      disabled={!canEdit}
                      label={
                        <span className="small">
                          <strong>{item.mcc}</strong>
                          {' - '}
                          {item.description || item.category}
                          {item.restricted === 'yes' && (
                            <span className="text-muted ms-1">
                              (default restricted)
                            </span>
                          )}
                        </span>
                      }
                    />
                  </div>
                ))}
              </div>
            )
          })}
        {!isLoading && groupedCodes.length === 0 && (
          <div className="text-muted text-center py-3 small">
            No matching MCC codes found
          </div>
        )}
      </div>

      {!hideApplyButton && (
        <div className="d-flex justify-content-between align-items-center gap-2">
          <span className="text-muted small">
            {currentValues.length} selected
          </span>
          {canEdit && (
            <Button variant="light" size="sm" onClick={handleClear}>
              Clear
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={canEdit ? handleApply : onClose}
          >
            {canEdit ? 'Apply' : 'Close'}
          </Button>
        </div>
      )}
    </div>
  )
}

export default MccSelectFilterPopover
